import React, { Suspense, lazy, useContext, useRef, useState } from 'react';
import clsx from 'clsx';
import { IconSort, Inline, useModal } from '@treatwell/ui';
import { Aggregations } from 'js/model/rainbow/browse-page/Aggregations';
import { CmsBrowsePage } from 'js/model/cms/cms-browse-page';
import { ConditionalWrapper } from 'js/components/Utilities/ConditionalWrapper';
import { Context } from 'js/components/LocaleWrapper';
import { SearchOptions } from 'js/model/rainbow/browse-page/SearchOptions';
import { VenueListingSpecificationOutput } from 'js/model/rainbow/browse-page/VenueListingSpecificationOutput';
import { Filter, useFilters } from '../Filters/useFilters';
import { FiltersButton } from './FiltersButton/FiltersButton';
import styles from './FiltersBar.module.css';
import { trackFilterClicked } from '../tracking/trackSearch';

const FiltersModal = lazy(
  () =>
    import(
      /* webpackChunkName: "FiltersModalRatingButtonExperiment" */ './FiltersModal'
    )
);

export const FiltersBar = ({
  specification,
  searchOptions,
  aggregations,
}: {
  specification: VenueListingSpecificationOutput;
  searchOptions: SearchOptions;
  aggregations: Aggregations;
}) => {
  const { pageData } = useContext(Context);
  const sortByButtonRef = useRef<HTMLButtonElement>(null);
  const buttonRefs = useRef<HTMLButtonElement[]>([]);
  const [filterId, setFilterId] = useState<Filter['id']>('sortBy');
  const cmsFilters = (pageData.cms as CmsBrowsePage).page.browse.filters;
  const {
    isOpen: isFiltersModalOpen,
    modalProps,
    openModal,
    setTriggerRef,
  } = useModal({
    title: cmsFilters['filter-button-label'],
    width: 600,
    height: '60vh',
  });
  const { filters, hasActiveFilters, clearFilters } = useFilters(
    aggregations,
    searchOptions,
    specification
  );
  const sortByFilter = filters[0];
  const filtersWithoutSortBy = filters.slice(1);

  const onOpenFiltersModal = (
    filterId: Filter['id'],
    button: HTMLButtonElement
  ) => {
    setTriggerRef(button);
    setFilterId(filterId);
    openModal();
    trackFilterClicked(
      pageData.channel.country.countryCode,
      specification,
      cmsFilters,
      filterId
    );
  };
  return (
    <div className={styles.root}>
      <div className={styles.filters}>
        <Inline space="xs" align="normal" className={styles.filtersBar}>
          <FiltersButton
            id="sortBy"
            chipLabel={sortByFilter.chipLabel}
            isActive={sortByFilter.isActive}
            dataCy="FiltersButton--sortBy"
            icon={<IconSort size={16} />}
            onClick={() => {
              if (sortByButtonRef.current) {
                onOpenFiltersModal(sortByFilter.id, sortByButtonRef.current);
              }
            }}
            ref={sortByButtonRef}
          />

          {filtersWithoutSortBy.length > 0 ? (
            <>
              <span className={styles.separator} />
              <ConditionalWrapper
                condition={hasActiveFilters}
                render={(children) => (
                  <Inline
                    space="xs"
                    justify="between"
                    className={styles.wrapper}
                  >
                    {children}
                  </Inline>
                )}
              >
                <Inline
                  space="xs"
                  as="ul"
                  className={clsx({
                    [styles.wrapper]: !hasActiveFilters,
                  })}
                >
                  {filtersWithoutSortBy.map((filter, index) => (
                    <li key={filter.id}>
                      <FiltersButton
                        id={filter.id}
                        chipLabel={filter.chipLabel}
                        isActive={filter.isActive}
                        count={filter.count}
                        dataCy={`FiltersButton--${filter.id}`}
                        onClick={() => {
                          onOpenFiltersModal(
                            filter.id,
                            buttonRefs.current[index]
                          );
                        }}
                        ref={(filtersButton) => {
                          if (filtersButton) {
                            buttonRefs.current[index] = filtersButton;
                          }
                        }}
                      />
                    </li>
                  ))}
                </Inline>

                {hasActiveFilters && (
                  <FiltersButton
                    chipLabel={cmsFilters['clear-filters']}
                    buttonStyle="Red"
                    onClick={clearFilters}
                    dataCy="FiltersButton--clearFiltersButton"
                  />
                )}
              </ConditionalWrapper>
            </>
          ) : null}
        </Inline>
      </div>
      {isFiltersModalOpen && (
        <Suspense>
          <FiltersModal
            modalProps={modalProps}
            filterId={filterId}
            aggregations={aggregations}
            searchOptions={searchOptions}
            specification={specification}
          />
        </Suspense>
      )}
    </div>
  );
};
